import isEmpty from "lodash/isEmpty";
import i18n from "../i18n";
import uploadOtherExerciseVideo from "../utils/upload-other-exercise-video";
import { getComplexName } from "../utils/complex";

const uploadVideo = async (body, files, api, user) => {
  const { videoUrl, videoDeleteOtherExercise } = body;

  let videoData;
  if (!videoUrl && !isEmpty(files)) {
    videoData = await uploadOtherExerciseVideo({ api, body, files, user });
  } else if (videoUrl) {
    videoData = {
      url: videoUrl,
    };
  }

  if (videoData) {
    return { video: videoData };
  }

  if (videoDeleteOtherExercise) {
    return { video: "" };
  }

  return {};
};

async function copySecondaryMovements(
  exercises,
  user,
  activeUserToRequest,
  api,
  proxyAthlete
) {
  if (!user || !exercises?.length) return;

  for (const exercise of exercises) {
    if (!exercise.primary || !exercise.secondary) continue;

    const { primary, secondary } = exercise;

    const coachLibrary = activeUserToRequest.exerciseLibrary?.[primary] || [];
    const coachMovement = coachLibrary.find(
      ({ movement }) => movement === secondary
    );
    if (!coachMovement) continue;

    const library = user.exerciseLibrary?.[primary] || [];
    const athleteMovement = library.find(
      ({ movement }) => movement === secondary
    );
    if (athleteMovement) continue;

    const newExercise = {
      ...coachMovement,
      repMaximum: user.repMaximums[primary] || 100,
    };

    try {
      await api(`exercise-library-secondary/${user._id}`, {
        method: "POST",
        ...(proxyAthlete ? { query: { proxyAthlete } } : {}),
        body: { primary, exercise: newExercise },
      });
    } catch (_) {
      // Best-effort: a concurrent request may have already added this
      // exact movement. The complex's exercises still save regardless.
    }
  }
}

const Complex = (app) => {
  app
    .post(
      "/complex/create/:id?",
      async ({ api, assert, flash, locals, req, res }) => {
        const { body, files, params } = req;
        const { exercises, loadBasedOn } = body;

        assert(
          exercises?.[0]?.primary,
          400,
          i18n.t("coaching-portal:complexActions.complex1Required")
        );
        assert(
          exercises?.[1]?.primary,
          400,
          i18n.t("coaching-portal:complexActions.complex2Required")
        );
        assert(
          loadBasedOn != null,
          400,
          i18n.t("coaching-portal:complexActions.loadBasedOnRequired")
        );

        const name = getComplexName({ exercises });

        const videoData = await uploadVideo(
          body,
          files,
          api,
          locals.activeUserToRequest
        );

        const submitBody = { ...videoData, exercises, loadBasedOn, name };

        if (params.id) {
          await api(`complex/${params.id}`, {
            method: "PUT",
            body: submitBody,
          });
        } else {
          await api("complex", {
            method: "POST",
            body: submitBody,
          });
        }

        flash(
          "success",
          i18n.t("coaching-portal:complexActions.successfullySaved")
        );

        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .get("/complex/delete/:id", async ({ api, assert, flash, req, res }) => {
      const { id } = req.params;
      assert(
        id,
        400,
        i18n.t("coaching-portal:complexActions.provideIdToDelete")
      );

      await api(`complex/${id}`, {
        method: "DELETE",
      });

      flash(
        "success",
        i18n.t("coaching-portal:complexActions.successfullyDeleted")
      );
      res.backDontScroll();
    })
    .post(
      "/complex/save",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { body, query = {} } = req;

        const organizationCoach = body.organizationCoach || undefined;

        await load("profileUser", { user: query.proxyAthlete });

        const { profileUser } = locals;

        const activeUser = profileUser || locals.activeUserToRequest;

        assert(
          body.exercises?.length > 1,
          400,
          i18n.t("coaching-portal:complexActions.addAtLeastTwoExercises")
        );

        for (const exercise of body.exercises) {
          exercise.user = activeUser._id;
          if (!exercise.cycle)
            assert(
              exercise.date,
              400,
              i18n.t("coaching-portal:complexActions.specifyDayForWorkout")
            );
          assert(
            exercise.primary,
            400,
            i18n.t("coaching-portal:complexActions.primaryExerciseRequired")
          );
          if (exercise.type === "secondary")
            assert(
              exercise.secondary,
              400,
              i18n.t("coaching-portal:complexActions.specifyExerciseName")
            );

          assert(
            exercise.sets?.length > 0,
            400,
            i18n.t("coaching-portal:complexActions.addAtLeastOneSet")
          );

          if (!exercise.repMaximums) {
            exercise.repMaximums = activeUser.repMaximums;
          }

          assert(
            !isEmpty(exercise.repMaximums),
            400,
            i18n.t("coaching-portal:complexActions.specifyRepMax")
          );

          exercise.sets.forEach((set) => {
            if (typeof set.reps === "string" && set.reps.includes("-")) {
              const [repsLower, reps] = set.reps.split("-");
              set.reps = reps;
              set.repsLower = repsLower;
            }
          });
        }

        const isNew = !body.exercisesToEdit;

        await Promise.all([
          copySecondaryMovements(
            body.exercises,
            profileUser,
            locals.activeUserToRequest,
            api,
            query.proxyAthlete
          ),
          api("add-complex", {
            method: isNew ? "POST" : "PUT",
            body: {
              exercises: body.exercises,
              proxyAthlete: query.proxyAthlete,
              organizationCoach,
              exercisesToEdit: body.exercisesToEdit,
            },
          }),
        ]);

        flash(
          "success",
          isNew
            ? i18n.t("coaching-portal:complexActions.successfullyAdded")
            : i18n.t("coaching-portal:complexActions.successfullyUpdated")
        );

        res.refreshWith(
          {
            modal: "",
            editMode: "",
            movement: "",
            secondaryAdded: "",
            addType: "",
            complex: "",
            exercises: "",
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post("/complex/remove", async ({ req, res, assert, flash, api }) => {
      const { body } = req;

      if (
        process.browser &&
        !confirm(i18n.t("coaching-portal:complexActions.confirmDeleteComplex"))
      ) {
        return res.backDontScroll();
      }

      assert(
        body.exercises?.length > 0,
        400,
        i18n.t("coaching-portal:complexActions.provideAtLeastOneExercise")
      );
      const exercises = body.exercises.map((ex) => ex._id);

      await api("exercise-remove-multiple", {
        method: "POST",
        body: { ...body, exercises },
      });

      flash(
        "success",
        i18n.t("coaching-portal:complexActions.successfullyRemoved")
      );
      res.backDontScroll();
    });
};

export default Complex;
